import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCamera, FiMapPin, FiClock, FiCheckCircle, FiAlertTriangle, FiMail, FiPhone, FiShield, FiUploadCloud, FiCheckSquare } from 'react-icons/fi';
import request from '../utils/request';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const scheduleArrayToMap = (arr = []) =>
  arr.reduce((acc, slot) => {
    if (!slot?.day) return acc;
    acc[slot.day] = {
      start: slot.start || '',
      end: slot.end || '',
      off: !!slot.off,
    };
    return acc;
  }, {});

const serializeSchedule = (draft = {}) =>
  WEEKDAYS.map(day => {
    const slot = draft[day] || {};
    return {
      day,
      start: slot.start || '',
      end: slot.end || '',
      off: !!slot.off,
    };
  });

export default function DispatcherProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [contactDraft, setContactDraft] = useState({});
  const [scheduleDraft, setScheduleDraft] = useState({});
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function fetchProfile() {
      setLoading(true);
      setError(null);
      try {
        const { data: profileRes } = await request.get('/employees/me');
        const employee = profileRes.employee || profileRes;
        if (!mounted) return;
        setProfile(employee);
        setContactDraft({
          phone: employee.phone || '',
          personalEmail: employee.personalEmail || '',
          workEmail: employee.workEmail || '',
          timezone: employee.timezone || '',
          location: employee.location || '',
          paypalEmail: employee.paypalEmail || '',
        });
        setScheduleDraft(scheduleArrayToMap(employee.schedule || []));

        if (employee._id) {
          const [{ data: statsRes }, { data: escRes }] = await Promise.all([
            request.get(`/employees/${employee._id}/stats`),
            request.get(`/employees/${employee._id}/escalations?limit=10`)
          ]);
          if (!mounted) return;
          setStats(statsRes.stats || statsRes);
          setEscalations(escRes.escalations || escRes.items || []);
        }
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || 'Failed to load profile');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchProfile();
    return () => { mounted = false; };
  }, []);

  const escalationWeekCount = useMemo(() => {
    return stats?.escalationWeekCount ?? profile?.escalationWeekCount ?? 0;
  }, [stats, profile]);

  const isOverThreshold = escalationWeekCount > 3;

  const handleAvatarChange = async (event) => {
    if (!profile?._id) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('avatar', file);
    setSaving(true);
    setError(null);
    try {
      const { data } = await request.post(`/employees/${profile._id}/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile(prev => ({ ...prev, avatar: data.employee?.avatar || data.avatar }));
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Avatar upload failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile?._id) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...contactDraft,
        schedule: serializeSchedule(scheduleDraft),
      };
      const { data } = await request.patch(`/employees/${profile._id}`, payload);
      const updated = data.employee || data;
      setProfile(updated);
      setScheduleDraft(scheduleArrayToMap(updated.schedule || []));
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const updateSchedule = (day, field, value) => {
    setScheduleDraft(prev => ({
      ...prev,
      [day]: { ...(prev?.[day] || {}), [field]: value }
    }));
  };

  const handleGovIdUpload = async (event) => {
    if (!profile?._id) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingId(true);
    setError(null);
    try {
      const { data } = await request.post(`/employees/${profile._id}/id-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const docUrl = data?.url || data?.location;
      setProfile(prev => ({ ...prev, govIdUrl: docUrl }));
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Government ID upload failed');
    } finally {
      setUploadingId(false);
    }
  };

  const checklist = [
    { label: 'PayPal email on file', complete: !!contactDraft.paypalEmail },
    { label: 'Compliance attested (VPN / Device / Policy)', complete: !!profile?.vpnAttestedAt && !!profile?.deviceAttestedAt && !!profile?.policyAcknowledgedAt },
    { label: 'Government ID uploaded', complete: !!profile?.govIdUrl },
    { label: 'Schedule set', complete: Object.values(scheduleDraft || {}).some(day => day?.start || day?.off) },
  ];

  if (loading) {
    return (
      <div className="p-6 text-gray-600 dark:text-gray-200">
        Loading profile...
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white p-6 space-y-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-bold shadow-lg">
            DP
          </div>
          <div>
            <h1 className="text-2xl font-bold">Dispatcher Profile</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Schedule, contact, and escalation metrics</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/onboarding')}
            className="px-3 py-2 bg-white text-indigo-700 border border-indigo-200 rounded-xl text-sm font-semibold hover:bg-indigo-50 shadow-sm"
          >
            Open onboarding hub
          </button>
          {justSaved && <span className="text-emerald-600 text-sm">Saved</span>}
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </header>

      {error && (
        <div className="p-3 rounded-xl bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="col-span-12 xl:col-span-8 space-y-4">
          {/* Metrics and onboarding */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/70 dark:border-gray-700 p-5">
              <h2 className="text-lg font-semibold mb-3">Metrics</h2>
              <div className="space-y-3">
                <MetricRow
                  label="Escalations this week"
                  value={escalationWeekCount}
                  tone={isOverThreshold ? 'red' : escalationWeekCount > 0 ? 'amber' : 'emerald'}
                  hint={isOverThreshold ? 'High usage — review before next escalation.' : 'Keep escalations rare; use checklists first.'}
                />
                <MetricRow label="Orders placed (MTD)" value={stats?.ordersPlacedMTD ?? '--'} />
                <MetricRow label="Gold stars" value={stats?.goldStars ?? 0} />
                <MetricRow label="Negative marks" value={stats?.negativeMarks ?? 0} />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/70 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">Onboarding checklist</h2>
                <span className="text-xs text-gray-500">{checklist.filter(i => i.complete).length}/{checklist.length} done</span>
              </div>
              <div className="space-y-2">
                {checklist.map(item => (
                  <div key={item.label} className="flex items-center gap-2 text-sm p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
                    <FiCheckSquare className={`w-4 h-4 ${item.complete ? 'text-emerald-500' : 'text-gray-400'}`} />
                    <span className={item.complete ? 'line-through text-gray-500' : ''}>{item.label}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/onboarding')}
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow hover:bg-indigo-700 transition-colors"
              >
                Continue onboarding
              </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/70 dark:border-gray-700 p-5 h-full">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">Compliance</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate('/signing/ops-guide')}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    Sign Ops Guide
                  </button>
                  <span className="text-xs text-gray-500">Uploads & attestations</span>
                </div>
              </div>
              <div className="space-y-3">
                <InputField
                  label="PayPal Email (payouts)"
                  icon={<FiMail className="w-4 h-4 text-gray-400" />}
                  value={contactDraft.paypalEmail}
                  onChange={v => setContactDraft(prev => ({ ...prev, paypalEmail: v }))}
                />
                <div className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700">
                    <FiShield className="w-4 h-4" />
                  </div>
                  <div className="text-sm flex-1">
                    <p className="font-medium">Government ID</p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs">Upload a photo of your government-issued ID (stored in S3).</p>
                    <div className="mt-2 flex items-center gap-2">
                      <label className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50">
                        <FiUploadCloud className="w-4 h-4" />
                        {uploadingId ? 'Uploading...' : 'Upload ID'}
                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleGovIdUpload} />
                      </label>
                      {profile?.govIdUrl && (
                        <a
                          href={profile.govIdUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-indigo-600 font-semibold hover:underline"
                        >
                          View uploaded ID
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Identity & contact */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/70 dark:border-gray-700 p-6 flex gap-6 h-full lg:col-span-1">
            <div className="flex flex-col items-center gap-3">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 text-white flex items-center justify-center text-3xl font-bold overflow-hidden">
                {profile?.avatar
                  ? <img src={profile.avatar} alt="avatar" className="w-full h-full object-cover" />
                  : `${profile?.firstName?.[0] || 'D'}${profile?.lastName?.[0] || 'P'}`}
              </div>
              <label className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600">
                <FiCamera className="w-4 h-4" />
                Upload Photo
                <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
              </label>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-4">
              <InfoRow label="Name" value={`${profile?.firstName || ''} ${profile?.lastName || ''}`} />
              <InfoRow label="Role" value={profile?.role || 'Dispatcher'} />
                <InfoRow label="Location" value={contactDraft.location || '—'} icon={<FiMapPin className="w-4 h-4 text-gray-400" />} />
              <InfoRow label="Timezone" value={contactDraft.timezone || '—'} icon={<FiClock className="w-4 h-4 text-gray-400" />} />
              <InfoRow label="Department" value={profile?.department || 'Operations'} />
              <InfoRow label="Status" value={profile?.status || 'active'} icon={<FiCheckCircle className="w-4 h-4 text-emerald-500" />} />
            </div>
          </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/70 dark:border-gray-700 p-5 lg:col-span-2">
              <h2 className="text-lg font-semibold mb-4">Contact</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Work Email"
                  icon={<FiMail className="w-4 h-4 text-gray-400" />}
                  value={contactDraft.workEmail}
                  onChange={v => setContactDraft(prev => ({ ...prev, workEmail: v }))}
                />
                <InputField
                  label="Personal Email"
                  icon={<FiMail className="w-4 h-4 text-gray-400" />}
                  value={contactDraft.personalEmail}
                  onChange={v => setContactDraft(prev => ({ ...prev, personalEmail: v }))}
                />
                <InputField
                  label="Phone"
                  icon={<FiPhone className="w-4 h-4 text-gray-400" />}
                  value={contactDraft.phone}
                  onChange={v => setContactDraft(prev => ({ ...prev, phone: v }))}
                />
                <InputField
                  label="Location"
                  icon={<FiMapPin className="w-4 h-4 text-gray-400" />}
                  value={contactDraft.location}
                  onChange={v => setContactDraft(prev => ({ ...prev, location: v }))}
                />
                <InputField
                  label="Timezone"
                  icon={<FiClock className="w-4 h-4 text-gray-400" />}
                  value={contactDraft.timezone}
                  onChange={v => setContactDraft(prev => ({ ...prev, timezone: v }))}
                />
              </div>
            </div>
        </div>

          {/* Schedule */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/70 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Schedule</h2>
              <span className="text-xs text-gray-500">Set start/end per day</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {WEEKDAYS.map(day => {
                const slot = scheduleDraft?.[day] || {};
                return (
                  <div key={day} className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
                    <p className="text-sm font-medium mb-2">{day}</p>
                    <div className="space-y-2">
                      <input
                        type="time"
                        value={slot.start || ''}
                        onChange={e => updateSchedule(day, 'start', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
                        placeholder="Start"
                      />
                      <input
                        type="time"
                        value={slot.end || ''}
                        onChange={e => updateSchedule(day, 'end', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
                        placeholder="End"
                      />
                      <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={!!slot.off}
                          onChange={e => updateSchedule(day, 'off', e.target.checked)}
                        />
                        Off / PTO
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="col-span-12 xl:col-span-4 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/70 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Recent Escalations</h2>
              <span className="text-xs text-gray-500">{escalations.length} records</span>
            </div>
            {escalations.length === 0 ? (
              <p className="text-sm text-gray-500">No escalations logged.</p>
            ) : (
              <div className="space-y-3">
                {escalations.map(item => (
                  <div key={`${item.ticketId}-${item.escalatedAt}`} className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">{item.reason || 'Reason not set'}</p>
                      <StatusPill status={item.status} />
                    </div>
                    <p className="text-xs text-gray-500">Ticket {item.ticketId || '—'} • Order {item.orderId || '—'}</p>
                    <p className="text-xs text-gray-500">Subreason: {item.subreason || '—'}</p>
                    <p className="text-xs text-gray-400 mt-1">{item.escalatedAt ? new Date(item.escalatedAt).toLocaleString() : '—'}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, icon }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {icon}
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="font-medium">{value || '—'}</p>
      </div>
    </div>
  );
}

function InputField({ label, value, onChange, icon }) {
  return (
    <label className="text-sm space-y-1">
      <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2">{icon}{label}</span>
      <input
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
        placeholder={label}
      />
    </label>
  );
}

function Badge({ label, active }) {
  return (
    <span className={`px-2 py-1 rounded-full text-[11px] font-medium ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
      {label}
    </span>
  );
}

function MetricRow({ label, value, hint, tone = 'default' }) {
  const toneClasses = {
    default: 'bg-gray-100 text-gray-800',
    amber: 'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-700',
  };
  return (
    <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
      <div>
        <p className="text-sm font-medium">{label}</p>
        {hint && <p className="text-xs text-gray-500">{hint}</p>}
      </div>
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${toneClasses[tone] || toneClasses.default}`}>
        {value ?? '--'}
      </span>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    resolved: 'bg-emerald-100 text-emerald-700',
    open: 'bg-amber-100 text-amber-700',
    pending: 'bg-sky-100 text-sky-700',
    rejected: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${map[status] || 'bg-gray-200 text-gray-600'}`}>
      {status || 'open'}
    </span>
  );
}

